import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_TEST_RESULTS } from '../graphql/queries';
import { DataTable } from '../components/DataTable';

function statusBadge(status) {
  const map = { passed: 'success', failed: 'error', skipped: 'pending' };
  return <span className={`badge badge-${map[status] || 'pending'}`}>{status}</span>;
}

const testColumns = [
  { accessorKey: 'name', header: 'Test Name' },
  { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => statusBadge(getValue()) },
  { accessorKey: 'message', header: 'Message', cell: ({ getValue }) => (
    <span style={{ fontSize: 12, color: '#636e72', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
      {getValue() || '-'}
    </span>
  )},
  { accessorKey: 'duration', header: 'Duration', cell: ({ getValue }) => getValue() || '-' },
];

export default function TestResults() {
  const { imageId } = useParams();
  const { data, loading, error } = useQuery(GET_TEST_RESULTS, { variables: { imageId } });

  if (loading) return <div className="loading">Loading test results...</div>;
  if (error) return <div className="error">Failed to load test results: {error.message}</div>;

  const results = data?.getTestResults;
  if (!results) {
    return (
      <div>
        <h2 className="page-title">Test Results</h2>
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: '#636e72' }}>No test results available for this image.</p>
          <Link to={`/images/${imageId}`} className="btn btn-primary" style={{ marginTop: 16 }}>Back to Image</Link>
        </div>
      </div>
    );
  }

  const passRate = results.totalTests > 0 ? Math.round((results.passed / results.totalTests) * 100) : 0;

  return (
    <div>
      <h2 className="page-title">Test Results</h2>

      <div className="stats-grid">
        <div className="stat-card" style={{ borderLeft: '4px solid #00b894' }}>
          <div className="stat-value" style={{ color: '#00b894' }}>{results.passed}</div>
          <div className="stat-label">Passed</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #d63031' }}>
          <div className="stat-value" style={{ color: '#d63031' }}>{results.failed}</div>
          <div className="stat-label">Failed</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #fdcb6e' }}>
          <div className="stat-value">{results.skipped}</div>
          <div className="stat-label">Skipped</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #0984e3' }}>
          <div className="stat-value">{passRate}%</div>
          <div className="stat-label">Pass Rate</div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0 }}>
              {statusBadge(results.status)} Test Suite
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#636e72' }}>
              Framework: {results.framework || 'Unknown'} | Total: {results.totalTests} tests
              {results.executedAt && ` | Executed: ${new Date(results.executedAt).toLocaleString()}`}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ width: '100%', height: 8, background: '#dfe6e9', borderRadius: 4, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ width: `${passRate}%`, background: '#00b894' }} />
            {results.failed > 0 && <div style={{ width: `${Math.round((results.failed / results.totalTests) * 100)}%`, background: '#d63031' }} />}
            {results.skipped > 0 && <div style={{ width: `${Math.round((results.skipped / results.totalTests) * 100)}%`, background: '#fdcb6e' }} />}
          </div>
        </div>

        <DataTable columns={testColumns} data={results.results || []} pageSize={20} />
      </div>

      <div className="actions">
        <Link to={`/images/${imageId}`} className="btn btn-primary">Back to Image</Link>
        <Link to={`/scorecard/${imageId}`} className="btn btn-primary" style={{ background: '#00b894' }}>Health Scorecard</Link>
      </div>
    </div>
  );
}
